/**
 * Strips credentials and personal data from an error-monitoring (Sentry)
 * event before it leaves the process. Used as `beforeSend` (and
 * `beforeSendTransaction`) by both the server and the browser.
 *
 * This module handles customers' cloud credentials (access keys, secret
 * keys, session tokens, client secrets, API keys), so it errs on the side
 * of removing too much:
 *  - request bodies and cookies are dropped outright;
 *  - only a short allowlist of request headers is kept;
 *  - any field whose name looks like a credential is replaced, at any depth;
 *  - query-string values whose names mention token/code/secret/key/... are
 *    replaced, in URLs anywhere in the event;
 *  - free text (messages, exception values, breadcrumbs) has known secret
 *    shapes replaced: bearer tokens, JWTs, private keys, URL passwords,
 *    AWS access keys and secret keys, Azure client secrets, and
 *    `name=value` / `"name": "value"` pairs with a credential-like name.
 *
 * No dependencies, no Sentry types: it works on plain objects so the same
 * file is used on the server and in the browser.
 */

export const FILTERED = "[Filtered]";

/** Request headers worth keeping; everything else is dropped. */
const SAFE_HEADERS = new Set([
  "accept",
  "accept-language",
  "content-length",
  "content-type",
  "host",
  "origin",
  "referer",
  "user-agent",
  "x-forwarded-proto",
  "x-request-id",
]);

/** Field names (lower-cased, punctuation removed) containing any of these are credentials. */
const SENSITIVE_NAME_PARTS = [
  "password",
  "passwd",
  "passphrase",
  "secret", // secretAccessKey, clientSecret, client_secret
  "token", // sessionToken, accessToken, refresh_token, id_token, sasToken
  "apikey", // apiKey, api_key, x-api-key, IBM Cloud apikey
  "accesskey", // accessKeyId, AWS_ACCESS_KEY_ID, sharedAccessKey
  "privatekey",
  "credential",
  "authorization",
  "cookie",
  "signature",
  "connectionstring",
  "sessionid",
  "bearer",
  "jwt",
  "externalid", // AWS role external ID
  "certificate", // Azure certificate credentials
];

/** Field names that are credentials only as a whole word. */
const SENSITIVE_NAMES_EXACT = new Set([
  "key",
  "keys",
  "auth",
  "pwd",
  "pass",
  "sig",
  "sas",
  "pem",
  "otp",
  "pin",
]);

/** Query parameter names (lower-cased) containing any of these have their values replaced. */
const SENSITIVE_PARAM_PARTS = [
  "token",
  "code",
  "secret",
  "key",
  "password",
  "passwd",
  "pwd",
  "auth",
  "sig",
  "credential",
  "session",
  "state",
];

const MAX_DEPTH = 12;

function normalise(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** True if a field with this name holds (or may hold) a credential. */
export function isSensitiveName(name: string): boolean {
  const n = normalise(name);
  if (!n) return false;
  if (SENSITIVE_NAMES_EXACT.has(n)) return true;
  if (n.endsWith("key") || n.endsWith("keys")) return true;
  return SENSITIVE_NAME_PARTS.some(part => n.includes(part));
}

function isSensitiveParam(name: string): boolean {
  let decoded = name;
  try {
    decoded = decodeURIComponent(name);
  } catch {
    // keep the raw name
  }
  const n = decoded.toLowerCase();
  return (
    isSensitiveName(decoded) ||
    SENSITIVE_PARAM_PARTS.some(part => n.includes(part))
  );
}

/** Replaces sensitive query (and fragment) parameter values in any text containing URLs. */
export function scrubUrl(text: string): string {
  return text.replace(
    /([?&#;])([^=&#?;\s"'<>]+)=([^&#;\s"'<>]*)/g,
    (match, sep: string, name: string) =>
      isSensitiveParam(name) ? `${sep}${name}=${FILTERED}` : match,
  );
}

/** Replaces known secret shapes in free text. */
export function scrubString(text: string): string {
  if (!text) return text;
  let out = text;
  // PEM private keys (whole block).
  out = out.replace(
    /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----[\s\S]*?(-----END [A-Z0-9 ]*PRIVATE KEY-----|$)/g,
    FILTERED,
  );
  // Authorization schemes.
  out = out.replace(
    /\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi,
    `$1 ${FILTERED}`,
  );
  // JWTs.
  out = out.replace(
    /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g,
    FILTERED,
  );
  // Passwords in URLs: scheme://user:password@host
  out = out.replace(
    /(\b[a-z][a-z0-9+.-]*:\/\/)[^/\s:@]+:[^/\s@]+@/gi,
    `$1${FILTERED}@`,
  );
  // Query and fragment parameters.
  out = scrubUrl(out);
  // name=value, name: value, "name": "value" with a credential-like name
  // (connection strings like AccountKey=...; included).
  out = out.replace(
    /(["']?)([A-Za-z_][A-Za-z0-9_.-]{1,63})\1(\s*[:=]\s*)(["']?)([^"'\s,;&}\]]+)/g,
    (
      match,
      q1: string,
      name: string,
      sep: string,
      q2: string,
      value: string,
    ) =>
      isSensitiveName(name) &&
      !value.startsWith(FILTERED.slice(0, -1)) &&
      !/^\/\//.test(value)
        ? `${q1}${name}${q1}${sep}${q2}${FILTERED}`
        : match,
  );
  // AWS access key IDs (long-term AKIA, temporary ASIA).
  out = out.replace(/\b(AKIA|ASIA)[A-Z0-9]{16}\b/g, FILTERED);
  // Azure AD client secrets (xxxNQ~...).
  out = out.replace(
    /(?<![A-Za-z0-9_~.-])[A-Za-z0-9_~.-]{3}[0-9]Q~[A-Za-z0-9_~.-]{30,40}/g,
    FILTERED,
  );
  // AWS secret access keys: 40 base64 characters with upper, lower and digits.
  out = out.replace(
    /(?<![A-Za-z0-9/+=])[A-Za-z0-9/+]{40}(?![A-Za-z0-9/+=])/g,
    match =>
      /[A-Z]/.test(match) && /[a-z]/.test(match) && /[0-9]/.test(match)
        ? FILTERED
        : match,
  );
  return out;
}

/** Deep copy of `value` with credential-like fields and secret-shaped text replaced. */
export function scrubValue(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (typeof value === "string") return scrubString(value);
  if (value === null || typeof value !== "object") return value;
  if (depth >= MAX_DEPTH) return FILTERED;
  if (seen.has(value)) return "[Circular]";
  seen.add(value);
  if (Array.isArray(value))
    return value.map(item => scrubValue(item, depth + 1, seen));
  const out: Record<string, unknown> = {};
  for (const [name, inner] of Object.entries(
    value as Record<string, unknown>,
  )) {
    out[name] =
      isSensitiveName(name) &&
      inner !== null &&
      inner !== undefined &&
      inner !== "" &&
      typeof inner !== "boolean"
        ? FILTERED
        : scrubValue(inner, depth + 1, seen);
  }
  return out;
}

type Bag = Record<string, unknown>;

function isBag(value: unknown): value is Bag {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function scrubQueryString(query: unknown): unknown {
  if (typeof query === "string") {
    const scrubbed = scrubUrl(query.startsWith("?") ? query : `?${query}`);
    return query.startsWith("?") ? scrubbed : scrubbed.slice(1);
  }
  if (Array.isArray(query)) {
    return query.map(pair =>
      Array.isArray(pair) &&
      typeof pair[0] === "string" &&
      isSensitiveParam(pair[0])
        ? [pair[0], FILTERED]
        : scrubValue(pair),
    );
  }
  if (isBag(query)) {
    const out: Bag = {};
    for (const [name, value] of Object.entries(query))
      out[name] = isSensitiveParam(name) ? FILTERED : scrubValue(value);
    return out;
  }
  return query;
}

function scrubRequest(request: Bag): Bag {
  const out: Bag = { ...request };
  delete out.cookies;
  delete out.data; // request bodies may carry credentials
  if ("data" in request) out.data = FILTERED;
  delete out.env;
  if (typeof out.url === "string") out.url = scrubString(out.url);
  if ("query_string" in out)
    out.query_string = scrubQueryString(out.query_string);
  if (isBag(out.headers)) {
    const headers: Bag = {};
    for (const [name, value] of Object.entries(out.headers)) {
      if (!SAFE_HEADERS.has(name.toLowerCase())) continue;
      headers[name] = typeof value === "string" ? scrubString(value) : value;
    }
    out.headers = headers;
  }
  return out;
}

/**
 * `beforeSend` / `beforeSendTransaction`: returns a scrubbed copy of the
 * event. Never throws; if scrubbing fails the event is dropped rather than
 * sent unscrubbed.
 */
export function scrubEvent<T>(event: T): T | null {
  try {
    if (!isBag(event)) return event;
    const out: Bag = { ...event };
    if (isBag(out.request)) out.request = scrubRequest(out.request);
    if (isBag(out.user)) {
      const user: Bag = { ...out.user };
      delete user.ip_address;
      delete user.email;
      delete user.username;
      out.user = scrubValue(user);
    }
    if (typeof out.message === "string") out.message = scrubString(out.message);
    for (const field of [
      "logentry",
      "extra",
      "contexts",
      "tags",
      "exception",
      "breadcrumbs",
      "spans",
    ]) {
      if (field in out) out[field] = scrubValue(out[field]);
    }
    if (typeof out.transaction === "string")
      out.transaction = scrubString(out.transaction);
    return out as T;
  } catch {
    return null;
  }
}

/** Reads a sample rate from the environment: 0 (errors only) unless a number in [0, 1] is given. */
export function parseSampleRate(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : 0;
}
